import type { Booking, BookingFlag, CustomerRecord, Deposit, MedicalProfile } from '@/domain/types';
import { depositPaidAud } from '@/utils/bookingSummary';

export interface ComputedFlag {
  kind: BookingFlag;
  source: 'derived';
  detail?: string;
}

const HOUR = 3_600_000;
const DAY = 86_400_000;

const money = (n: number) => `$${Math.round(n)}`;
const titleCase = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Short one-line medical summary: allergies + top condition (or medication). */
export function medicalSummary(mp: MedicalProfile): string | undefined {
  const parts: string[] = [];
  if (mp.allergies?.length) parts.push(`${titleCase(mp.allergies.join(', '))} allergy`);
  const topCondition = mp.conditions?.[0];
  const topMedication = mp.medications?.[0];
  if (topCondition) parts.push(topCondition);
  else if (topMedication) parts.push(`on ${topMedication}`);
  if (!parts.length && mp.notes?.trim()) parts.push(mp.notes.trim());
  return parts.length ? parts.join(' · ') : undefined;
}

export function medicalIsEmpty(mp: MedicalProfile): boolean {
  return (
    !mp.allergies?.length &&
    !mp.medications?.length &&
    !mp.conditions?.length &&
    !mp.notes?.trim()
  );
}

/**
 * Derives the operational flags for a booking — purely *facts* about the booking
 * that are always true given its data (medical note, short deposit, last-minute
 * fill). Checkout-time actions (rebook, deposit rollover) live in the floor
 * checkout flow (`sessionFlow.ts`), not here.
 *
 * `siblingBookings` is the pool of other bookings used for context — pass the full
 * booking set; the function narrows to the same artist as needed (for late-fill).
 */
export function computeBookingFlags(
  booking: Booking,
  customer: CustomerRecord | undefined,
  deposit: Deposit | undefined,
  siblingBookings: Booking[],
): ComputedFlag[] {
  const startMs = new Date(booking.start).getTime();
  const artistBookings = siblingBookings.filter(
    (b) => b.artistId === booking.artistId && b.id !== booking.id,
  );

  const flags: ComputedFlag[] = [];

  // Medical note: any populated clinical field on the customer's medical profile.
  const mp = customer?.medicalProfile;
  if (mp && !medicalIsEmpty(mp)) {
    flags.push({ kind: 'medical-note', source: 'derived', detail: medicalSummary(mp) });
  }

  // Deposit top-up pending: paid ledger is short of the agreed deposit amount.
  if (deposit) {
    const paid = depositPaidAud(deposit);
    if (paid < deposit.amountAud) {
      flags.push({
        kind: 'deposit-topup-pending',
        source: 'derived',
        detail: `${money(deposit.amountAud - paid)} outstanding`,
      });
    }
  }

  // Late fill: booked within 48h of the slot AND a same-artist cancellation freed
  // a nearby slot in the prior 7 days.
  const bookedLate = Math.abs(startMs - new Date(booking.createdAt).getTime()) <= 48 * HOUR;
  const recentCancellation = artistBookings.some((b) => {
    if (b.status !== 'cancelled') return false;
    const gap = startMs - new Date(b.start).getTime();
    return gap >= 0 && gap <= 7 * DAY;
  });
  if (bookedLate && recentCancellation) {
    flags.push({ kind: 'late-fill', source: 'derived' });
  }

  return flags;
}
